import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2, XCircle, ArrowRight } from "lucide-react";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [state, setState] = useState("checking"); // checking | paid | failed
  const attempts = useRef(0);
  const sessionId = params.get("session_id");
  const boqEstimate = typeof window !== "undefined" ? localStorage.getItem("bg_boq_estimate") : null;
  const planPurchase = typeof window !== "undefined" ? localStorage.getItem("bg_plan_purchase") : null;
  const projectId = typeof window !== "undefined" ? localStorage.getItem("bg_pending_project") : null;

  useEffect(() => {
    if (!sessionId) { setState("failed"); return; }
    let timer;
    const poll = async () => {
      attempts.current += 1;
      try {
        const res = await api.get(`/payments/status/${sessionId}`);
        if (res.data.payment_status === "paid") { setState("paid"); return; }
        if (["failed", "expired"].includes(res.data.payment_status)) { setState("failed"); return; }
      } catch {}
      if (attempts.current >= 12) { setState("failed"); return; }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  const goBack = () => {
    if (boqEstimate) {
      localStorage.removeItem("bg_boq_estimate");
      navigate(`/estimate?boq=${boqEstimate}`);
      return;
    }
    if (planPurchase) {
      localStorage.removeItem("bg_plan_purchase");
      navigate("/dashboard?tab=plans");
      return;
    }
    navigate(projectId ? `/project/${projectId}` : "/dashboard");
  };

  return (
    <div className="min-h-screen bg-[#FBFAF9] flex items-center justify-center px-6">
      <div className="card-premium rounded-2xl p-10 max-w-md w-full text-center" data-testid="payment-result">
        {state === "checking" && (
          <>
            <Loader2 className="h-12 w-12 text-[#0E9AC8] animate-spin mx-auto mb-5" strokeWidth={1.5} />
            <h1 className="font-display text-2xl font-bold text-slate-900">Confirming your payment…</h1>
            <p className="mt-2 text-slate-500">This only takes a moment.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <div className="h-14 w-14 rounded-2xl bg-emerald-50 flex items-center justify-center mx-auto mb-5">
              <CheckCircle2 className="h-8 w-8 text-emerald-600" strokeWidth={1.75} />
            </div>
            <h1 className="font-display text-2xl font-bold text-slate-900">Payment successful!</h1>
            <p className="mt-2 text-slate-500">{boqEstimate ? <>Your <span className="font-semibold text-slate-700">AI Bill of Quantities</span> is unlocked. We'll generate it now.</> : planPurchase ? <>Your <span className="font-semibold text-slate-700">Advisory Plan is active</span>. Download your invoice anytime from My Plans.</> : <>Your consultation is <span className="font-semibold text-slate-700">Paid — Confirmed</span>. Our expert will contact you before your slot.</>}</p>
            <Button data-testid="success-continue" onClick={goBack} className="mt-6 bg-[#0E9AC8] hover:bg-[#0B7EA4] text-white rounded-xl transition-colors">
              {boqEstimate ? "View my BOQ" : planPurchase ? "View My Plans" : "Go to project"} <ArrowRight className="ml-2 h-4 w-4" strokeWidth={2} />
            </Button>
          </>
        )}
        {state === "failed" && (
          <>
            <div className="h-14 w-14 rounded-2xl bg-red-50 flex items-center justify-center mx-auto mb-5">
              <XCircle className="h-8 w-8 text-red-600" strokeWidth={1.75} />
            </div>
            <h1 className="font-display text-2xl font-bold text-slate-900">Payment not confirmed</h1>
            <p className="mt-2 text-slate-500">We couldn't confirm your payment yet. If you were charged, it will reflect shortly.</p>
            <Button data-testid="failed-continue" onClick={goBack} variant="outline" className="mt-6 rounded-xl">Back to project</Button>
          </>
        )}
      </div>
    </div>
  );
}
